import React from "react";
import { format } from "date-fns";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar } from "../ui/calendar";
import { Plus, Camera, X, Calendar as CalendarIcon } from "lucide-react";

/**
 * Shared form fields for adding / editing a defect.
 * `value` is the defect-form object, `setValue` is its React state setter.
 * When `showAddButtons` is true, "+" buttons appear next to Area/Element/Issue
 * (used by the Add dialog). `unassignedValue` is what `assigned_to` is set to
 * when "Unassigned" is selected ("" for Add, null for Edit).
 */
export const DefectFormFields = ({
  value,
  setValue,
  areas,
  elements,
  issues,
  contractors,
  showAddButtons = false,
  onAddArea,
  onAddElement,
  onAddIssue,
  showStatus = false,
  unassignedValue = "",
  imageInputId,
  onImageUpload,
  onRemoveImage,
}) => {
  const patch = (field, v) => setValue((prev) => ({ ...prev, [field]: v }));
  const images = value.images || [];

  return (
    <div className="space-y-4 py-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Area *</Label>
          <div className="flex gap-2">
            <Select
              value={value.area}
              onValueChange={(v) => patch("area", v)}
            >
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Select area..." />
              </SelectTrigger>
              <SelectContent>
                {areas.map((area) => (
                  <SelectItem key={area.id} value={area.name}>
                    {area.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {showAddButtons && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onAddArea}
              >
                <Plus className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
        <div>
          <Label>Element *</Label>
          <div className="flex gap-2">
            <Select
              value={value.element}
              onValueChange={(v) => patch("element", v)}
            >
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Select element..." />
              </SelectTrigger>
              <SelectContent>
                {elements.map((element) => (
                  <SelectItem key={element.id} value={element.name}>
                    {element.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {showAddButtons && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onAddElement}
              >
                <Plus className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Issue *</Label>
          <div className="flex gap-2">
            <Select
              value={value.issue}
              onValueChange={(v) => patch("issue", v)}
            >
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Select issue..." />
              </SelectTrigger>
              <SelectContent>
                {issues.map((issue) => (
                  <SelectItem key={issue.id} value={issue.name}>
                    {issue.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {showAddButtons && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onAddIssue}
              >
                <Plus className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
        <div>
          <Label>Priority</Label>
          <Select
            value={value.priority}
            onValueChange={(v) => patch("priority", v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {showStatus && (
        <div>
          <Label>Status</Label>
          <Select
            value={value.status}
            onValueChange={(v) => patch("status", v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Assigned To (Contractor)</Label>
          <Select
            value={value.assigned_to || "__none__"}
            onValueChange={(v) =>
              patch("assigned_to", v === "__none__" ? unassignedValue : v)
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select contractor..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Unassigned</SelectItem>
              {contractors.map((contractor) => (
                <SelectItem key={contractor.id} value={contractor.id}>
                  {contractor.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Due Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {value.due_date ? format(value.due_date, "d MMMM yyyy") : "Pick a date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={value.due_date}
                onSelect={(date) => patch("due_date", date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div>
        <Label>Details</Label>
        <Textarea
          placeholder="Additional details about the defect"
          value={value.details || ""}
          onChange={(e) => patch("details", e.target.value)}
          rows={3}
        />
      </div>

      {/* Image Upload */}
      <div>
        <Label>Photos (Max 3)</Label>
        <div className="flex flex-col gap-2 mt-2">
          <div className="flex gap-2">
            <input
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              onChange={onImageUpload}
              style={{ display: "none" }}
              id={imageInputId}
              disabled={images.length >= 3}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => document.getElementById(imageInputId).click()}
              disabled={images.length >= 3}
              className="flex items-center gap-2"
            >
              <Camera className="w-4 h-4" />
              {images.length >= 3 ? "Max Images" : "Add Photo"}
            </Button>
            <span className="text-xs text-slate-500 self-center">
              {images.length}/3 images
            </span>
          </div>
          {images.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {images.map((img, index) => (
                <div key={index} className="relative group">
                  <img
                    src={img}
                    alt={`Defect ${index + 1}`}
                    className="w-full h-24 object-cover rounded border"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => onRemoveImage(index)}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
